/**
 * Configuración de "Pedidos por WhatsApp" guardada en menus.template_settings.ordering.
 * Default SEGURO: apagado. Ninguna cuenta existente activa pedidos por accidente.
 * El país del número es explícito (country_code); México es sólo el default.
 */

import { DEFAULT_DIAL_CODE, sanitizeDialCode } from './whatsapp';

export type OrderMode = 'table' | 'pickup' | 'delivery';

export interface OrderingSettings {
  enabled: boolean;
  table: boolean;
  pickup: boolean;
  delivery: boolean;
  /** Lada país en dígitos, sin "+". Se usa sólo si el número es nacional. */
  countryCode: string;
}

export const DEFAULT_ORDERING_SETTINGS: OrderingSettings = {
  enabled: false,
  table: false,
  pickup: false,
  delivery: false,
  countryCode: DEFAULT_DIAL_CODE,
};

/** Valores sugeridos la primera vez que el restaurante enciende pedidos. */
export const SUGGESTED_ON_SETTINGS: OrderingSettings = {
  enabled: true,
  table: true,
  pickup: true,
  delivery: false,
  countryCode: DEFAULT_DIAL_CODE,
};

const asBool = (value: unknown): boolean => value === true;

/** Lee `template_settings` (o directamente el nodo `ordering`) sin confiar en su forma. */
export function parseOrderingSettings(raw: unknown): OrderingSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_ORDERING_SETTINGS };
  const container = raw as Record<string, unknown>;
  const node = (container.ordering && typeof container.ordering === 'object')
    ? container.ordering as Record<string, unknown>
    : container;

  const modes = (node.modes && typeof node.modes === 'object')
    ? node.modes as Record<string, unknown>
    : node;

  return {
    enabled: asBool(node.enabled),
    table: asBool(modes.table),
    pickup: asBool(modes.pickup),
    delivery: asBool(modes.delivery),
    countryCode: node.country_code ? sanitizeDialCode(node.country_code) : DEFAULT_DIAL_CODE,
  };
}

/** Escribe el nodo `ordering` dentro de template_settings preservando el resto. */
export function withOrderingSettings(
  templateSettings: unknown,
  ordering: OrderingSettings
): Record<string, unknown> {
  const base = (templateSettings && typeof templateSettings === 'object')
    ? { ...(templateSettings as Record<string, unknown>) }
    : {};
  return {
    ...base,
    ordering: {
      enabled: ordering.enabled,
      country_code: sanitizeDialCode(ordering.countryCode),
      modes: {
        table: ordering.table,
        pickup: ordering.pickup,
        delivery: ordering.delivery,
      },
    },
  };
}


export function enabledModes(settings: OrderingSettings): OrderMode[] {
  const modes: OrderMode[] = [];
  if (settings.table) modes.push('table');
  if (settings.pickup) modes.push('pickup');
  if (settings.delivery) modes.push('delivery');
  return modes;
}

/**
 * Los pedidos sólo operan si están encendidos, hay al menos una modalidad
 * y el restaurante tiene un WhatsApp utilizable.
 */
export function isOrderingActive(settings: OrderingSettings, whatsappDigits: string | null): boolean {
  return settings.enabled && enabledModes(settings).length > 0 && !!whatsappDigits;
}

export const ORDER_MODE_LABELS: Record<OrderMode, string> = {
  table: 'En mesa',
  pickup: 'Para recoger',
  delivery: 'A domicilio',
};
